import { Root, RootContent } from "mdast";
import { fromMarkdown } from "mdast-util-from-markdown";
import { visit } from "unist-util-visit";

import { preprocessMarkdown } from "./preprocess";

/**
 * Try to fix some common issues in the markdown content.
 * Most of the issues stand from the fact that that we purposely disable some markdown features
 * (like headers, blockquotes, images, etc.) in the editor.
 *
 * This function is invoked conditionally if unsupported node types are detected.
 */
function correctMarkdown(input: string): string {
    const correctors = [
        correctHeaders,
        correctHeadersInListItems,
        correctBlockquotes,
        correctBlockquotesInListItems,
        correctImages,
        correctThematicBreaks,
        correctCodeBlocksWithBackticks,
        correctCodeBlocksWithIndentations,
        correctCodeBlocksWithTildes,
        correctHtmlTags,
        correctLinkDefinitions,
    ];

    return correctors.reduce((output, correct) => correct(output), input);
}

/**
 * Convert at the beginning of the line.
 * - "#" => "\#"
 * - "=" => "\="
 *
 * Convert if the previous line is not empty and the line that starts with `-` has nothing else on it.
 * - "-" => "\-"
 */
function correctHeaders(input: string): string {
    return input
        .replace(/^(\s*)#/gm, "$1\\#")
        .replace(/^(\s*)=/gm, "$1\\=")
        .replace(/^(?!\s*-|\s*$)(.+)\n^([\t ]*)(-+\s*)$/gm, "$1\n$2\\$3");
}

/**
 *
 * Encode headers in lists, so `#` is not interpreted as a header.
 * Example input:
 *     `- # foo`
 *  Output:
 *     `- \# foo`
 * NOTE: There is a plenty of ways to define a list:
 *     `- Foo`
 *     `* Foo`
 *     `+ Foo`
 *     `1. Foo`
 *     `1) Foo`
 */
function correctHeadersInListItems(markdown: string): string {
    return markdown.replace(/^(\s*)(-|\*|\+|\d+\.|\d+\))(\s+)#/gm, "$1$2$3\\#");
}

/**
 * Convert `>` => `\>` at the beginning of the line.
 */
function correctBlockquotes(input: string): string {
    return input.replace(/^(\s*)>/gm, "$1\\>");
}

/**
 * Escapes blockquotes (`>`) in list items.
 * - `- > 10M` => `- \> 10M`
 * - `* > 10M` => `* \> 10M`
 * - `1) > 10M` => `1) \> 10M`
 * - `1. > 10M` => `1. \> 10M`
 */
function correctBlockquotesInListItems(input: string): string {
    return input.replace(/^(\s*)(-|\*|\+|\d+\.|\d+\)) >/gm, "$1$2 \\>");
}

/**
 * Convert: "![*](" => "!\[*]\("
 */
function correctImages(input: string): string {
    return input.replace(/!\[([^\]]*)]\(/gm, "!\\[$1]\\(");
}

/**
 * Convert:
 * - "--" => "\--" at the beginning of the line.
 * - "***" => "\*\*\*"  if there is nothing else on the line.
 * - "___" => "\_\_\_"  if there is nothing else on the line.
 * - "- - -" => "\- - -"
 * - "* * *" => "\* * *"
 * - * * * * *" => "\* * * * *"
 * - And so on...
 */
function correctThematicBreaks(input: string): string {
    return input
        .replace(/^(\s*)--/gm, "$1\\--")
        .replace(/^(\s*)((?:_\s*){3,})$/gm, "$1\\$2")
        .replace(/^(\s*)((?:-\s*){3,})$/gm, "$1\\$2")
        .replace(/^(\s*)((?:\*\s*){3,})$/gm, "$1\\$2");
}

/**
 * Escape multiple backticks.
 * Note: a single backtick does not require escaping.
 */
function correctCodeBlocksWithBackticks(input: string): string {
    return input.replace(/(`{2,})/gm, (match) => match.replace(/`/g, "\\`"));
}

/**
 * Code blocks that start with 4 space identification.
 * Convert "    " => "&#x20;   " at the beginning of the line.
 * Convert "\t"   => ""&#x9;" at the beginning of the line.
 */
function correctCodeBlocksWithIndentations(input: string): string {
    return input
        .replace(/^ ( {3})/gm, "&#x20;$1")
        .replace(/^(\t)/gm, "&#x9;")
        .replace(/^ ( *)\t/gm, "&#x20;$1\t");
}

/**
 * Convert `~~~` => `\~~~` at the beginning of the line.
 */
function correctCodeBlocksWithTildes(input: string): string {
    return input.replace(/^(\s*)~~~/gm, "$1\\~~~");
}

/**
 *  Convert "<" => "\<"
 */
function correctHtmlTags(input: string): string {
    return input.replace(/(?<!\\)</gm, "\\<");
}

/**
 * Escape link definitions (allowing whitespaces at the beginning and end of the line).
 * Convert "[link name]: link-url" => "\[link name]: link-url"
 */
function correctLinkDefinitions(input: string): string {
    return input.replace(/^(\s*)\[([^\]]*)]:/gm, "$1\\[$2]:");
}

/**
 * The type of the node in the markdown AST.
 * See https://github.com/syntax-tree/mdast for more information.
 */
type MarkdownNodeType = Root["type"] | RootContent["type"];

/**
 * The list of nodes that Impero supports in the markdown content.
 * This has implications for `MarkdownEditor` component and `renderMarkdown()` function.
 *
 * If this list is changed, `correctMarkdown()` function needs to be updated correspondingly.
 */
const SUPPORTED_NODE_TYPES: Array<MarkdownNodeType> = [
    "root",
    "paragraph",
    "link",
    "text",
    "strong",
    "emphasis",
    "list",
    "listItem",
    "inlineCode",
    "break",
];

/**
 * Parse the markdown content and return the set of node types found in the content.
 * This function is used to detect unsupported node types in the markdown content.
 */
export function detectMarkdownNodeTypes(
    markdownText: string
): Set<MarkdownNodeType> {
    const foundNodeTypes = new Set<MarkdownNodeType>();

    const tree: Root = fromMarkdown(markdownText);
    visit(tree, (node) => {
        foundNodeTypes.add(node.type);
    });

    return foundNodeTypes;
}

/**
 * Result produced by the `inspectMarkdown()` function.
 */
interface MarkdownInspectionResult {
    /**
     * Whether the markdown content is OK (i.e., it does not contain unsupported node types).
     */
    isOk: boolean;

    /**
     * The list of unsupported node types found in the markdown content if any.
     */
    unsupportedNodeTypes: Array<MarkdownNodeType>;
}

/**
 * Inspect the markdown content for unsupported node types
 */
function inspectMarkdown(markdownText: string): MarkdownInspectionResult {
    const nodeTypes = detectMarkdownNodeTypes(markdownText);
    const unsupportedNodeTypes = Array.from(nodeTypes).filter(
        (nodeType) => !SUPPORTED_NODE_TYPES.includes(nodeType)
    );
    return {
        isOk: unsupportedNodeTypes.length === 0,
        unsupportedNodeTypes,
    };
}

interface SanitizeMarkdownArgs {
    /**
     * Markdown content to sanitize.
     */
    markdownText: string;

    /**
     * Callback to invoke when the sanitization fails.
     * It's meant to be used to report potential issues to Sentry or other error tracking services.
     */
    onFailure?: (msg: string) => void;
}

/**
 * Sanitize the markdown content by escaping unsupported node types.
 * If the content stills contains unsupported node types after sanitization, the `onFailure` callback is invoked.
 */
function sanitizeMarkdown({
    markdownText,
    onFailure,
}: SanitizeMarkdownArgs): string {
    const preprocessedMarkdown = preprocessMarkdown(markdownText);

    if (inspectMarkdown(preprocessedMarkdown).isOk) {
        // If no problems are found, we can return the original content.
        return preprocessedMarkdown;
    }

    // Otherwise try to correct the markdown content.
    const correctedMarkdownText = correctMarkdown(preprocessedMarkdown);
    // If the correction did not help, we should invoke the callback to report
    // the incident.
    if (onFailure) {
        const { isOk, unsupportedNodeTypes } = inspectMarkdown(
            correctedMarkdownText
        );
        if (!isOk) {
            const msg =
                "Unsupported node types found after sanitization: " +
                unsupportedNodeTypes.join(", ");
            onFailure(msg);
        }
    }
    return correctedMarkdownText;
}

export { sanitizeMarkdown };
